/**
 * WHAT THE TEACHER TYPES IN, AND WHAT mhchem GETS OUT.
 *
 * ── THE COMPLAINT THIS ANSWERS ────────────────────────────────────────────────────────────────
 * Pressing the Δ button put `\Delta` in the field. Pressing the equilibrium arrow put `<=>`.
 * Every insert showed its own source, so the editor read as a LaTeX box wearing a chemistry
 * costume — "teacher unfriendly" was the owner's word for it, and that is the whole of the
 * problem being fixed here.
 *
 * A token's `text` is the DISPLAY form now: Δ, ⇌, ²⁺. mhchem is produced at the boundary, on the
 * way to storage, and read back on the way in. Nothing in the editing surface handles source.
 *
 * ── WHY THE TABLE IS THE PALETTE ──────────────────────────────────────────────────────────────
 * The mapping is not invented here. Every palette button already carries both halves, a `sym` to
 * show and a `code` to insert, so the table is read out of the palette — which means a symbol
 * cannot be added with a display form that nothing converts, because there is only one place to
 * add it.
 *
 * ── AND WHY IT CHECKS ITSELF AGAINST A CORPUS ────────────────────────────────────────────────
 * Checking each rule alone is not enough, because what breaks is the INTERACTION. `e^-{}`
 * round-trips perfectly by itself and still corrupts `2e^-`, which has no braces: the display
 * `e⁻` comes back as the electron particle rather than the `e` and charge it started as.
 *
 * That particular one is now deliberate — the scripts pass below shows `2e^-` as `2e⁻` and the
 * electron rule claims it back as `2e^-{}`, which is the safer spelling and the same ink. The
 * POINT stands: a rule is safe or unsafe only in company.
 *
 * So rules are admitted one at a time, longest code first, and a candidate is kept only if the
 * whole corpus still round-trips with it in — every code the palette can insert, frames
 * included, plus real reactions. A rule that would break something already working is simply
 * not admitted, and its symbol shows its source, which is the behaviour this file replaces and
 * is safe by construction.
 *
 * A few characters are excluded outright rather than tested, because they ARE mhchem's syntax
 * and no round-trip check catches a wrong READING. `^` and `v` mean gas and precipitate standing
 * alone but superscript and a letter everywhere else, and `^{}_{}` survives being shown as
 * `↑{}_{}` while being plainly wrong. Scripts are handled by a pass instead, further down, which
 * is how `^{238}` can be shown without `^` ever becoming a rule. A hyphen is a bond, a charge and a range all at once, so
 * showing the bond's en dash turned chloride's `Cl-` into `Cl–`.
 *
 * ── WHAT STILL SHOWS ITS STRUCTURE, AND WHY THAT IS NOT THE SAME THING ───────────────────────
 * A frame with a hole in it — `->[ ]`, a nuclide's two slots — keeps its brackets, because the
 * hole is the point: it is where the catalyst goes. Brackets are notation a chemist reads.
 * Backslashes and `$…$` are what this gets rid of.
 */

import { CATEGORIES } from './reaction-symbols';

/**
 * Spellings a teacher may type or paste, accepted on the way IN only.
 *
 * One-directional on purpose. KaTeX draws the long arrow, so that is what comes back from a copy
 * of a rendered reaction, and it has to be understood — but the palette's own `→` is what gets
 * shown, so these never run in the display direction or the two arrows would fight.
 */
const TYPED_ALIASES: [string, string][] = [
  ['⟶', '->'],
  ['⟵', '<-'],
  ['−', '-'],      // a real minus sign, which is what a word processor substitutes
];

/**
 * The swap, and where every character ENDED UP.
 *
 * The map is not a nicety. A mark is a range of offsets into the token's text, so converting
 * `->[\Delta]` to `→Δ` moves every offset after it — a colour put on a fragment would come back
 * pointing at different characters, or past the end. Both directions carry the map so marks move
 * with the text they were put on.
 */
function swapMapped(text: string, rules: [string, string][]): { text: string; map: number[] } {
  let out = '';
  const map: number[] = new Array(text.length + 1).fill(-1);
  let i = 0;
  outer: while (i < text.length) {
    for (const [from, to] of rules) {
      if (from && text.startsWith(from, i)) {
        // Everything inside a replaced run collapses to where the run begins.
        for (let k = 0; k < from.length; k++) map[i + k] = out.length;
        out += to;
        i += from.length;
        continue outer;
      }
    }
    map[i] = out.length;
    out += text[i];
    i++;
  }
  map[text.length] = out.length;
  for (let k = text.length - 1; k >= 0; k--) if (map[k] === -1) map[k] = map[k + 1]!;
  return { text: out, map };
}

const swap = (text: string, rules: [string, string][]) => swapMapped(text, rules).text;

/** Longest first in both directions, so `<=>` never matches inside `<=>>`. */
const rulesFrom = (pairs: [string, string][]) => ({
  toDisplay: [...pairs].sort((a, b) => b[0].length - a[0].length),
  toSource: [...pairs].sort((a, b) => b[1].length - a[1].length)
    .map(([code, sym]) => [sym, code] as [string, string]),
});

/** mhchem's own syntax characters. See the note above on why no round-trip test catches these. */
const NEVER_MAP = new Set(['^', 'v', '_', '-']);

/** Real reactions a rule has to leave intact, beside every code the palette can insert. */
const CORPUS = [
  '2H2 + O2 -> 2H2O',
  'Zn -> Zn^2+ + 2e^-',
  '^{238}_{92}U -> ^{234}_{90}Th + \\alpha',
  'CaCO3 ->[\\Delta] CaO + CO2',
  'A ->[cat][sol] B',
  'Ca^2+ + 2OH^- -> Ca(OH)2',
  'CuSO4 * 5H2O',
  'CH3-C#C-CH3',
  'Zn(s) $|$ Zn^2+(aq) $\\|$ Cu(s)',
];

function buildTable(): { toDisplay: [string, string][]; toSource: [string, string][] } {
  const seen = new Set<string>();
  const candidates: [string, string][] = [];

  for (const category of CATEGORIES) {
    for (const item of category.items) {
      /*
       * A frame keeps its source, and a BORROWED glyph may not claim a character another row
       * owns. Being drawn is not a reason to leave the table: ⇄ and ⇌ are drawn in the editor
       * only because no font carries them, and every surface WITHOUT node views still has to
       * render `N2 <=> 2NH3` as a reaction rather than as its source.
       */
      if (item.cursor !== undefined || item.borrowed) continue;
      if (item.sym === item.code || seen.has(item.sym) || NEVER_MAP.has(item.code)) continue;
      seen.add(item.sym);
      candidates.push([item.code, item.sym]);
    }
  }
  const corpus = [
    ...CATEGORIES.flatMap(c => c.items.map(i => i.code)),
    ...CORPUS,
  ];
  const holds = (pairs: [string, string][]) => {
    const rules = rulesFrom(pairs);
    return corpus.every(c => swap(swap(c, rules.toDisplay), rules.toSource) === c);
  };

  // Longest code first, so the specific rule gets its chance before the general one.
  const accepted: [string, string][] = [];
  for (const candidate of [...candidates].sort((a, b) => b[0].length - a[0].length)) {
    if (holds([...accepted, candidate])) accepted.push(candidate);
  }

  const rules = rulesFrom(accepted);
  const aliases = TYPED_ALIASES.filter(([typed]) => !rules.toSource.some(([sym]) => sym === typed));
  return {
    toDisplay: rules.toDisplay,
    toSource: [...rules.toSource, ...aliases].sort((a, b) => b[0].length - a[0].length),
  };
}

const TABLE = buildTable();

/** Every display glyph the editor knows, for tests and for anyone extending the palette. */
export const DISPLAY_GLYPHS = TABLE.toSource.map(([sym]) => sym);

/**
 * SCRIPTS, WHICH THE TABLE CANNOT HOLD.
 *
 * ── WHY NOT JUST MORE RULES ───────────────────────────────────────────────────────────────────
 * The table is a list of whole strings, so it can hold `^2-` ↔ `²⁻` and does. What it cannot
 * hold is `^{2-}` or `^{238}` or `_n`, because a script takes ARBITRARY content and a list of
 * pairs is finite — there are four charges in it and nothing else. So a charge from the palette
 * showed as `²⁻` while the same charge written with braces showed as the literal characters
 * `^`, `{`, `2`, `-`, `}`, which is the LaTeX-in-a-chemistry-costume this file exists to end.
 * Isotopes were the worst of it: `^{238}_{92}U` is a nuclide every chemistry teacher writes and
 * it showed every brace.
 *
 * So scripts are a PASS rather than rules, run after the table in both directions — the
 * specific rule gets its chance before the general one, which is the order `buildTable` already
 * follows. Run the other way round and `–NH₂` loses its own rule to this one and comes back as
 * `-NH_2`, and the electron loses the `{}` that keeps `2e^-` from being misread.
 *
 * ── WHAT IT WILL NOT DO ───────────────────────────────────────────────────────────────────────
 * Only runs where EVERY character has a script form. Unicode's superscripts are a historical
 * accident, not an alphabet — there is no `ᵈ`, no `ᵠ`, no capital anything — so `^{Fe}` stays
 * source rather than coming out half-scripted and wrong. That is the behaviour everything
 * already had, so a run this cannot show loses nothing.
 *
 * A LONE `^` is untouched, because standing alone it is mhchem's gas arrow and not a script at
 * all. Same for `v`. This pass fires only where something scriptable follows.
 *
 * ── WHAT CHANGES IN STORAGE, AND WHY IT IS SAFE ───────────────────────────────────────────────
 * The round trip is not character-exact: `^{2-}` comes back `^2-` and `2e^-` comes back
 * `2e^-{}`. Both are mhchem spelling the pass normalises, and the RENDER is identical — checked
 * over the whole palette and the corpus, where the three that change spelling produce the same
 * ink. The second is the safer spelling of the two, which is why the electron rule carries it.
 */
const SUP: Record<string, string> = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸',
  '9': '⁹', '+': '⁺', '-': '⁻', '=': '⁼', '(': '⁽', ')': '⁾', 'n': 'ⁿ', 'i': 'ⁱ', 'x': 'ˣ',
  'a': 'ᵃ', 'b': 'ᵇ', 'c': 'ᶜ', 'd': 'ᵈ', 'e': 'ᵉ', 'm': 'ᵐ', 'y': 'ʸ', 'z': 'ᶻ',
};
const SUB: Record<string, string> = {
  '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈',
  '9': '₉', '+': '₊', '-': '₋', '=': '₌', '(': '₍', ')': '₎', 'n': 'ₙ', 'i': 'ᵢ', 'x': 'ₓ',
  'a': 'ₐ', 'e': 'ₑ', 'm': 'ₘ', 'o': 'ₒ', 'p': 'ₚ', 's': 'ₛ', 't': 'ₜ',
};
const UN_SUP = Object.fromEntries(Object.entries(SUP).map(([k, v]) => [v, k]));
const UN_SUB = Object.fromEntries(Object.entries(SUB).map(([k, v]) => [v, k]));

/** The script form of a string, or null when any character has none. */
export function toScript(text: string, kind: 'sup' | 'sub'): string | null {
  const table = kind === 'sup' ? SUP : SUB;
  const out = [...text].map(c => table[c]);
  return out.every(Boolean) ? out.join('') : null;
}

/** Whether every character is already scripted, which is what makes the buttons a toggle. */
export const isScripted = (text: string, kind: 'sup' | 'sub') =>
  text.length > 0 && [...text].every(c => (kind === 'sup' ? UN_SUP : UN_SUB)[c]);

/** The plain form of a scripted string. */
export const fromScript = (text: string, kind: 'sup' | 'sub') =>
  [...text].map(c => (kind === 'sup' ? UN_SUP : UN_SUB)[c] ?? c).join('');

/**
 * `^{2-}` and `^2-` → `²⁻`.
 *
 * The bare form takes the whole charge rather than one character, because that is what mhchem
 * reads: `PO4^3-` is phosphate, not a scripted 3 beside a stray minus.
 */
function scriptsToDisplay(source: string): { text: string; map: number[] } {
  const map: number[] = new Array(source.length + 1).fill(-1);
  let out = '';
  let i = 0;
  while (i < source.length) {
    const mark = source[i];
    if (mark === '^' || mark === '_') {
      const kind = mark === '^' ? 'sup' : 'sub';
      let body: string | undefined;
      let len = 0;
      if (source[i + 1] === '{') {
        const close = source.indexOf('}', i + 2);
        if (close !== -1 && !source.slice(i + 2, close).includes('{')) {
          body = source.slice(i + 2, close);
          len = close + 1 - i;
        }
      } else {
        const run = /^[0-9]*[+-]?/.exec(source.slice(i + 1))?.[0] ?? '';
        /* One character when there is no charge-shaped run, so `_n` works as well as `^2-`. */
        const taken = run || source[i + 1] || '';
        if (taken) { body = taken; len = 1 + taken.length; }
      }
      const scripted = body ? toScript(body, kind) : null;
      if (scripted) {
        /* Everything inside the run collapses to where it begins, as in `swapMapped`. */
        for (let k = 0; k < len; k++) map[i + k] = out.length;
        out += scripted;
        i += len;
        continue;
      }
    }
    map[i] = out.length;
    out += source[i];
    i++;
  }
  map[source.length] = out.length;
  for (let k = source.length - 1; k >= 0; k--) if (map[k] === -1) map[k] = map[k + 1]!;
  return { text: out, map };
}

/** `²⁻` → `^2-`. A maximal run, so `²³⁸` is one script and not three. */
function scriptsToSource(display: string): { text: string; map: number[] } {
  const map: number[] = new Array(display.length + 1).fill(-1);
  let out = '';
  let i = 0;
  while (i < display.length) {
    const kind = UN_SUP[display[i]!] ? 'sup' : UN_SUB[display[i]!] ? 'sub' : null;
    if (kind) {
      const un = kind === 'sup' ? UN_SUP : UN_SUB;
      let j = i;
      while (j < display.length && un[display[j]!]) j++;
      const body = display.slice(i, j).split('').map(c => un[c]!).join('');
      /*
       * Braces unless the body is spelled bare everywhere else already: a charge (`^2-`, `^+`)
       * or a single character (`_n`). Anything longer keeps them, so `^{238}` comes back as
       * `^{238}` — mhchem reads `^238` the same way, but rewriting a teacher's isotope into a
       * spelling they did not use is a change with nothing to show for it.
       */
      const bare = /^[0-9]?[+-]$/.test(body) || [...body].length === 1;
      const written = (kind === 'sup' ? '^' : '_') + (bare ? body : `{${body}}`);
      for (let k = i; k < j; k++) map[k] = out.length;
      out += written;
      i = j;
      continue;
    }
    map[i] = out.length;
    out += display[i];
    i++;
  }
  map[display.length] = out.length;
  for (let k = display.length - 1; k >= 0; k--) if (map[k] === -1) map[k] = map[k + 1]!;
  return { text: out, map };
}

/** Two passes, one offset map. A mark put on the second has to land through the first. */
function compose(
  a: { text: string; map: number[] },
  b: (text: string) => { text: string; map: number[] },
): { text: string; map: number[] } {
  const second = b(a.text);
  return { text: second.text, map: a.map.map(o => second.map[o] ?? second.text.length) };
}

/**
 * A HYPHEN BETWEEN TWO BONDED THINGS IS A BOND, AND THE FIELD SHOULD SAY SO.
 *
 * mhchem already renders it as one -- `CH3-CH3` and `CH3\bond{-}CH3` draw byte-identically --
 * so the output was never wrong. What was wrong is the EDITING VIEW: the button's face reads
 * `–` and pressing it put a plain ASCII hyphen in the field, which is the same character a
 * minus sign is, so a teacher could not see that a bond had been inserted at all.
 *
 * ── WHY A PASS AND NOT A TABLE ROW ────────────────────────────────────────────────────────────
 * `-` is in `NEVER_MAP` and has to stay there. The table runs BEFORE the scripts pass, so a
 * table row would reach `2e^-` while the charge is still spelled with a hyphen: it would become
 * `2e^–`, and `scriptsToDisplay` -- which knows `-` and not `–` -- would stop turning it into
 * `2e⁻`. The charge would lose its superscript to the bond rule.
 *
 * After the scripts pass there is no such hyphen left to confuse: a charge is already `⁻`, an
 * arrow is already `→`. Whatever `-` survives both is a bond or a stray minus, and mhchem reads
 * those the same way.
 *
 * ── EVERY SURVIVING HYPHEN, NOT ONLY A FLANKED ONE ────────────────────────────────────────────
 * This used to require a species on each side, to protect `Cl-` — chloride written the short
 * way — from being read as chlorine bonded to nothing. ASKING THE RENDERER SETTLED IT: mhchem
 * draws `Cl-`, `CH3-CH3` and `-CO-` with the same character, U+2212, and `Cl-` comes out
 * identical to `Cl^-`. There was never a distinction in the output to protect; the editing
 * view was the only place the two looked different.
 *
 * So the flanking rule did not make `Cl-` safer, it only made a typed `-CO-` show ASCII
 * hyphens next to an inserted `–CO–` — the same dash in the same formula, drawn two ways,
 * depending on which one had something on both sides of it. Every hyphen that reaches here is
 * a bond, and a bond is written like the double and triple ones beside it.
 *
 * It also has to run AFTER the table and not before it on the way back, because a palette code
 * can carry the glyph in its own name: `-NH2` displays as `–NH₂`, and converting the dash first
 * left a string the table no longer recognised, which came back as `-NH_2`.
 *
 * One character for one character, so the offset map every mark depends on is untouched.
 */
const BOND = '\u2013';
const bondOut = (text: string) => text.split('-').join(BOND);
const bondIn = (text: string) => text.split(BOND).join('-');

/** mhchem as stored → what the teacher sees and edits. */
export function toDisplay(source: string): string {
  return bondOut(scriptsToDisplay(swap(source, TABLE.toDisplay)).text);
}

/** What the teacher typed → mhchem, for storage and for the renderer. */
export function toSource(display: string): string {
  return bondIn(scriptsToSource(swap(display, TABLE.toSource)).text);
}

/** Both conversions, with the offset map a caller needs to carry marks across. */
export const toDisplayMapped = (source: string) => {
  const out = compose(swapMapped(source, TABLE.toDisplay), scriptsToDisplay);
  return { ...out, text: bondOut(out.text) };
};
export const toSourceMapped = (display: string) => {
  const out = compose(swapMapped(display, TABLE.toSource), scriptsToSource);
  return { ...out, text: bondIn(out.text) };
};
